import type { AudioVideoObserver } from 'amazon-chime-sdk-js';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useReducer } from 'react';
import { useAudioVideo } from '../AudioVideoProvider';
import type { State } from './state';
import { VideoTileActionType, initialState, reducer } from './state';

const Context = createContext<State | null>(null);

export const RemoteVideoTileProvider: FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const audioVideo = useAudioVideo();
  const [state, dispatch] = useReducer(
    reducer,
    initialState,
    () => initialState,
  );

  useEffect(() => {
    if (!audioVideo) {
      return;
    }

    const observer: AudioVideoObserver = {
      videoTileDidUpdate: (tileState): void => {
        if (
          tileState?.boundAttendeeId &&
          tileState?.tileId &&
          !tileState.isContent &&
          !tileState.localTile
        ) {
          const { tileId, boundAttendeeId } = tileState;

          dispatch({
            type: VideoTileActionType.UPDATE,
            payload: {
              tileId,
              attendeeId: boundAttendeeId,
            },
          });
        }
      },
    };

    audioVideo.addObserver(observer);
    return () => {
      audioVideo.removeObserver(observer);
      dispatch({ type: VideoTileActionType.RESET });
    };
  }, [audioVideo]);

  return <Context.Provider value={state}>{children}</Context.Provider>;
};

export const useRemoteVideoTileState = (): State => {
  const state = useContext(Context);

  if (!state) {
    throw new Error(
      'useRemoteVideoTileState must be used within a RemoteVideoTileProvider',
    );
  }

  return state;
};
